import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ConfirmModal from "../components/ConfirmModal";
import { JatsaConexionBadge } from "../components/JatsaIngestaEstado";
import planillaService from "../services/planillaService";
import planillaCierreService from "../services/planillaCierreService";
import fechasComputoRhService from "../services/fechasComputoRhService";
import { useAuth } from "../hooks/useAuth";
import {
  anexarMensajeJatsa,
  mensajeReaperturaJatsa,
  mostrarConexionJatsa,
} from "../utils/jatsaIngestaVisible";

const Planillas = () => {
  const navigate = useNavigate();
  const { selectedCompany } = useAuth();
  const [planillas, setPlanillas] = useState([]);
  const [jatsaIngestaEnabled, setJatsaIngestaEnabled] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [opciones, setOpciones] = useState({
    estados: {},
    modalidades: {},
  });
  const [filters, setFilters] = useState({
    dateType: "month",
    selectedDate: new Date().toISOString().split("T")[0],
    estado: "all",
  });
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    type: "warning",
    title: "",
    message: "",
    onConfirm: null,
    planillaId: null,
    showComments: true,
  });

  const [newPlanilla, setNewPlanilla] = useState({
    nombre: planillaService.generarNombrePlanilla(),
    descripcion: "",
    mes: new Date().getMonth() + 1, // Mes actual (1-12)
    año: new Date().getFullYear(), // Año actual
    modalidad_planilla: "planilla_normal",
    computo_rh_id: "",
    fecha_inicio: "",
    fecha_fin: "",
  });
  const [fechasComputoRh, setFechasComputoRh] = useState([]);
  const [loadingFechasComputoRh, setLoadingFechasComputoRh] = useState(false);

  // Cargar opciones para formularios
  useEffect(() => {
    const cargarOpciones = async () => {
      try {
        const response = await planillaService.obtenerOpciones();
        if (response.success) {
          setOpciones(response.data);
        }
      } catch (error) {
        console.error("Error al cargar opciones:", error);
      }
    };

    cargarOpciones();
  }, []);

  // Cargar planillas cuando cambie la empresa seleccionada
  useEffect(() => {
    if (selectedCompany?.id) {
      cargarPlanillas();
      cargarFechasComputoRh();
    }
  }, [selectedCompany]);

  const cargarFechasComputoRh = async () => {
    if (!selectedCompany?.id) return;
    try {
      setLoadingFechasComputoRh(true);
      const response = await fechasComputoRhService.obtenerFechas(selectedCompany.id);
      if (response.success) {
        setFechasComputoRh(response.data || []);
      } else {
        setFechasComputoRh([]);
      }
    } catch (error) {
      console.error("Error al cargar fechas de cómputo RH:", error);
      setFechasComputoRh([]);
    } finally {
      setLoadingFechasComputoRh(false);
    }
  };

  const cargarPlanillas = async () => {
    if (!selectedCompany?.id) {
      console.log("⚠️ No hay empresa seleccionada");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      console.log("🏢 Cargando planillas para empresa:", selectedCompany);
      const response = await planillaService.obtenerPlanillasPorEmpresa(selectedCompany.id);

      if (response.success) {
        console.log("✅ Planillas cargadas:", response.data);
        setPlanillas(response.data);
        setJatsaIngestaEnabled(Boolean(response.jatsa_ingesta_enabled));
      } else {
        console.error("❌ Error en la respuesta:", response.message);
        setPlanillas([]);
        setJatsaIngestaEnabled(false);
      }
    } catch (error) {
      console.error("❌ Error al cargar planillas:", error);
      setPlanillas([]);
      setJatsaIngestaEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  const formatDateToDDMMYYYY = (dateString) => {
    if (!dateString) return "N/A";
    return planillaService.formatearFecha(dateString);
  };

  const getEstadoBadge = (estado) => {
    const estadoConfig = {
      proceso: { class: "bg-blue-100 text-blue-800", text: "En Proceso", icon: "⚙️" },
      cerrada: { class: "bg-green-100 text-green-800", text: "Cerrada", icon: "✅" },
      anulada: { class: "bg-red-100 text-red-800", text: "Anulada", icon: "❌" },
    };
    return estadoConfig[estado] || estadoConfig.proceso;
  };

  const getModalidadBadge = (modalidad) => {
    const modalidadConfig = {
      planilla_normal: { class: "bg-blue-100 text-blue-800", text: "Normal" },
      planilla_rh: { class: "bg-purple-100 text-purple-800", text: "RH" },
      planilla_mixta: { class: "bg-orange-100 text-orange-800", text: "Mixta" },
      planilla_construccion: { class: "bg-amber-100 text-amber-800", text: "Construcción" },
      planilla_jornales: { class: "bg-emerald-100 text-emerald-800", text: "Jornales" },
    };
    return modalidadConfig[modalidad] || modalidadConfig.planilla_normal;
  };

  const esModalidadFechasLibres = (modalidad) =>
    modalidad === "planilla_construccion" || modalidad === "planilla_jornales";

  const isoLocal = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewPlanilla((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "modalidad_planilla" && esModalidadFechasLibres(value) && !prev.fecha_inicio) {
        if (value === "planilla_jornales") {
          const ahora = new Date();
          const inicio = new Date(ahora.getFullYear(), ahora.getMonth(), 1);
          const fin = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);
          next.fecha_inicio = isoLocal(inicio);
          next.fecha_fin = isoLocal(fin);
        } else {
          const hoy = new Date();
          const fin = new Date(hoy);
          fin.setDate(hoy.getDate() + 6);
          next.fecha_inicio = isoLocal(hoy);
          next.fecha_fin = isoLocal(fin);
        }
      }
      if (
        name === "modalidad_planilla" &&
        value === "planilla_jornales" &&
        /^\d{4}-\d{2}$/.test(String(prev.nombre || ""))
      ) {
        next.nombre = `${prev.nombre}-JOR`;
      }
      if (
        name === "modalidad_planilla" &&
        value !== "planilla_jornales" &&
        /^\d{4}-\d{2}-JOR$/.test(String(prev.nombre || ""))
      ) {
        next.nombre = String(prev.nombre).replace(/-JOR$/, "");
      }
      return next;
    });
  };

  // Función para calcular primer y último día del mes
  const calcularFechasMes = (mes, año) => {
    // Primer día del mes (siempre es día 1)
    const fechaInicio = new Date(año, mes - 1, 1);
    // Último día del mes (usando el día 0 del mes siguiente)
    const fechaFin = new Date(año, mes, 0);

    // Formatear como YYYY-MM-DD para el backend
    const formatearFecha = (fecha) => {
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, "0");
      const day = String(fecha.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      fecha_inicio: formatearFecha(fechaInicio),
      fecha_fin: formatearFecha(fechaFin),
    };
  };

  const calcularFechasPorComputoRh = (mes, año, diaComputo) => {
    const dia = Number(diaComputo);
    if (!dia || dia < 1 || dia > 31) return calcularFechasMes(mes, año);

    const lastDayInicio = new Date(año, mes, 0).getDate();
    const diaInicio = Math.min(dia, lastDayInicio);
    const fechaInicio = new Date(año, mes - 1, diaInicio);

    let añoFin = año;
    let mesFin = mes + 1;
    if (mesFin > 12) {
      mesFin = 1;
      añoFin += 1;
    }

    const lastDayFin = new Date(añoFin, mesFin, 0).getDate();
    const diaTope = Math.min(dia, lastDayFin);
    const fechaTope = new Date(añoFin, mesFin - 1, diaTope);
    const fechaFin = new Date(fechaTope);
    fechaFin.setDate(fechaFin.getDate() - 1);

    const formatearFecha = (fecha) => {
      const year = fecha.getFullYear();
      const month = String(fecha.getMonth() + 1).padStart(2, "0");
      const day = String(fecha.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    };

    return {
      fecha_inicio: formatearFecha(fechaInicio),
      fecha_fin: formatearFecha(fechaFin),
    };
  };

  const computoRhSeleccionado = fechasComputoRh.find(
    (item) => String(item.id) === String(newPlanilla.computo_rh_id),
  );

  const periodoPreview = (() => {
    if (newPlanilla.modalidad_planilla === "planilla_rh" && computoRhSeleccionado) {
      return calcularFechasPorComputoRh(
        Number(newPlanilla.mes),
        Number(newPlanilla.año),
        Number(computoRhSeleccionado.dia_inicio_computo),
      );
    }
    return calcularFechasMes(Number(newPlanilla.mes), Number(newPlanilla.año));
  })();

  // Función para formatear fecha directamente (DD/MM/YYYY)
  const formatearFechaDirecta = (fecha) => {
    const day = String(fecha.getDate()).padStart(2, "0");
    const month = String(fecha.getMonth() + 1).padStart(2, "0");
    const year = fecha.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Función para obtener el nombre del mes
  const getNombreMes = (numeroMes) => {
    const meses = [
      "Enero",
      "Febrero",
      "Marzo",
      "Abril",
      "Mayo",
      "Junio",
      "Julio",
      "Agosto",
      "Septiembre",
      "Octubre",
      "Noviembre",
      "Diciembre",
    ];
    return meses[numeroMes - 1];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validaciones
    if (!newPlanilla.nombre || newPlanilla.nombre.trim() === "") {
      alert("Por favor ingresa un nombre para la planilla");
      return;
    }

    if (!newPlanilla.descripcion || newPlanilla.descripcion.trim() === "") {
      alert("Por favor ingresa una descripción para la planilla");
      return;
    }

    const usaFechasLibres = esModalidadFechasLibres(newPlanilla.modalidad_planilla);
    if (usaFechasLibres) {
      const etiquetaFechas =
        newPlanilla.modalidad_planilla === "planilla_jornales"
          ? "la planilla de jornales"
          : "la planilla de construcción";
      if (!newPlanilla.fecha_inicio || !newPlanilla.fecha_fin) {
        alert(`Selecciona la fecha de inicio y la fecha fin de ${etiquetaFechas}`);
        return;
      }
      if (newPlanilla.fecha_fin < newPlanilla.fecha_inicio) {
        alert("La fecha fin debe ser igual o posterior a la fecha de inicio");
        return;
      }
    } else if (!newPlanilla.mes || !newPlanilla.año) {
      alert("Por favor selecciona el mes y año de la planilla");
      return;
    }

    try {
      const fechasCalculadas = usaFechasLibres
        ? { fecha_inicio: newPlanilla.fecha_inicio, fecha_fin: newPlanilla.fecha_fin }
        : newPlanilla.modalidad_planilla === "planilla_rh" && computoRhSeleccionado
          ? calcularFechasPorComputoRh(
              Number(newPlanilla.mes),
              Number(newPlanilla.año),
              Number(computoRhSeleccionado.dia_inicio_computo),
            )
          : calcularFechasMes(Number(newPlanilla.mes), Number(newPlanilla.año));

      const datosPlanilla = {
        nombre: newPlanilla.nombre,
        descripcion: newPlanilla.descripcion,
        fecha_inicio: fechasCalculadas.fecha_inicio,
        fecha_fin: fechasCalculadas.fecha_fin,
        modalidad_planilla: newPlanilla.modalidad_planilla,
        empresa_id: selectedCompany.id,
      };

      console.log("📝 Creando planilla para empresa:", selectedCompany);
      console.log("📅 Período calculado:", fechasCalculadas);

      const response = await planillaService.crearPlanilla(datosPlanilla);

      if (response.success) {
        alert("Planilla creada exitosamente");
        resetModal();
        cargarPlanillas(); // Recargar la lista
      } else {
        alert(response.message || "Error al crear la planilla");
      }
    } catch (error) {
      console.error("Error al crear planilla:", error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Error al crear la planilla");
      }
    }
  };

  const resetModal = () => {
    setNewPlanilla({
      nombre: planillaService.generarNombrePlanilla(),
      descripcion: "",
      mes: new Date().getMonth() + 1,
      año: new Date().getFullYear(),
      modalidad_planilla: "planilla_normal",
      computo_rh_id: "",
      fecha_inicio: "",
      fecha_fin: "",
    });
    setShowModal(false);
  };

  const handleVerDetalle = (planillaId) => {
    navigate(`/planillas/${planillaId}`);
  };

  const handleCerrarPlanilla = (planillaId) => {
    setConfirmModal({
      isOpen: true,
      type: "success",
      title: "Cerrar Planilla",
      message: "¿Está seguro que desea cerrar esta planilla? Esta acción no se puede deshacer.",
      onConfirm: (comentarios) => confirmarCerrarPlanilla(planillaId, comentarios),
      planillaId: planillaId,
      showComments: true,
    });
  };

  const handleAbrirPlanilla = (planillaId) => {
    setConfirmModal({
      isOpen: true,
      type: "warning",
      title: "Abrir Planilla",
      message: anexarMensajeJatsa(
        "¿Está seguro que desea abrir esta planilla? Podrá realizar modificaciones nuevamente.",
        mensajeReaperturaJatsa(jatsaIngestaEnabled),
      ),
      onConfirm: () => confirmarAbrirPlanilla(planillaId),
      planillaId: planillaId,
    });
  };

  const confirmarCerrarPlanilla = async (planillaId, comentarios) => {
    try {
      const response = await planillaService.cambiarEstado(planillaId, "cerrada", comentarios);

      if (response.success) {
        closeConfirmModal(); // Cerrar el modal primero
        alert("Planilla cerrada exitosamente");
        cargarPlanillas(); // Recargar la lista
      } else {
        alert(response.message || "Error al cerrar la planilla");
      }
    } catch (error) {
      console.error("Error al cerrar planilla:", error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Error al cerrar la planilla");
      }
    }
  };

  const confirmarAbrirPlanilla = async (planillaId) => {
    try {
      console.log(`🔓 Intentando reabrir planilla ${planillaId}`);

      const response = await planillaCierreService.reabrirPlanilla(planillaId, "");

      if (response.success) {
        closeConfirmModal(); // Cerrar el modal primero

        // Mostrar información detallada del resultado
        const estadisticas = response.estadisticas;
        let mensaje = response.message || "Planilla reabierta exitosamente";

        if (estadisticas) {
          mensaje += `\n\nDatos eliminados:`;
          mensaje += `\n• ${estadisticas.empleados_eliminados} empleados cerrados`;
          mensaje += `\n• ${estadisticas.conceptos_eliminados} conceptos calculados`;
          mensaje += `\n• ${estadisticas.eventos_eliminados} eventos de tareo`;
          mensaje += `\n• ${estadisticas.horas_extras_eliminadas} registros de horas extras`;
        }

        mensaje = anexarMensajeJatsa(
          mensaje,
          mensajeReaperturaJatsa(response.jatsa_ingesta_enabled ?? jatsaIngestaEnabled),
        );

        alert(mensaje);
        cargarPlanillas(); // Recargar la lista
      } else {
        // Manejar errores controlados del backend
        let mensajeError = response.error || "Error al reabrir la planilla";
        if (response.detalle_error) {
          mensajeError += `\n\nDetalle: ${response.detalle_error}`;
        }
        if (response.data?.mensaje_usuario) {
          mensajeError += `\n\n${response.data.mensaje_usuario}`;
        }
        if (response.data?.recomendacion) {
          mensajeError += `\n\nRecomendación: ${response.data.recomendacion}`;
        }

        alert(mensajeError);
      }
    } catch (error) {
      console.error("💥 Error inesperado al reabrir planilla:", error);
      alert("Error inesperado al reabrir la planilla. Revisa la consola para más detalles.");
    }
  };

  const handleEliminarPlanilla = (planillaId) => {
    setConfirmModal({
      isOpen: true,
      type: "warning",
      title: "Eliminar Planilla",
      message: "¿Está seguro que desea eliminar esta planilla? Esta acción no se puede deshacer.",
      onConfirm: () => confirmarEliminarPlanilla(planillaId),
      planillaId: planillaId,
    });
  };

  const confirmarEliminarPlanilla = async (planillaId) => {
    try {
      const response = await planillaService.eliminarPlanilla(planillaId);

      if (response.success) {
        closeConfirmModal(); // Cerrar el modal primero
        alert("Planilla eliminada exitosamente");
        cargarPlanillas(); // Recargar la lista
      } else {
        alert(response.message || "Error al eliminar la planilla");
      }
    } catch (error) {
      console.error("Error al eliminar planilla:", error);
      if (error.response?.data?.message) {
        alert(error.response.data.message);
      } else {
        alert("Error al eliminar la planilla");
      }
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal({
      isOpen: false,
      type: "warning",
      title: "",
      message: "",
      onConfirm: null,
      planillaId: null,
    });
  };

  const filteredPlanillas = planillas.filter((planilla) => {
    if (filters.estado === "all") return true;
    return planilla.estado === filters.estado;
  });

  const stats = {
    total: filteredPlanillas.length,
    proceso: filteredPlanillas.filter((p) => p.estado === "proceso").length,
    cerrada: filteredPlanillas.filter((p) => p.estado === "cerrada").length,
    anulada: filteredPlanillas.filter((p) => p.estado === "anulada").length,
  };

  // Si no hay empresa seleccionada
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="flex flex-col items-center justify-center h-64">
          <div className="text-gray-400 mb-4">
            <svg
              className="w-16 h-16 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No hay empresa seleccionada</h3>
          <p className="text-gray-500 text-center">
            Selecciona una empresa para ver las planillas.
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Cargando planillas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3 lg:space-y-4">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Planillas</h1>
          <p className="text-gray-600">Gestión de planillas de pago</p>
          {selectedCompany && (
            <p className="text-sm text-gray-500">Empresa: {selectedCompany.name}</p>
          )}
          {mostrarConexionJatsa(jatsaIngestaEnabled) && (
            <div className="mt-2">
              <JatsaConexionBadge />
            </div>
          )}
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center justify-center space-x-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-lg font-medium hover:from-teal-600 hover:to-teal-700 transition-all duration-200"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 6v6m0 0v6m0-6h6m-6 0H6"
            />
          </svg>
          <span>Nueva Planilla</span>
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Total</p>
              <p className="text-lg lg:text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-blue-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">En Proceso</p>
              <p className="text-lg lg:text-2xl font-bold text-blue-600">{stats.proceso}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl p-3 lg:p-4 shadow-sm border border-gray-200">
          <div className="flex flex-col lg:flex-row lg:items-center">
            <div className="p-2 bg-green-100 rounded-lg mb-2 lg:mb-0 lg:mr-4">
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div className="text-center lg:text-left">
              <p className="text-xs lg:text-sm font-medium text-gray-600">Cerradas</p>
              <p className="text-lg lg:text-2xl font-bold text-green-600">{stats.cerrada}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 lg:p-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Estado</label>
            <select
              value={filters.estado}
              onChange={(e) => setFilters((prev) => ({ ...prev, estado: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            >
              <option value="all">Todos los estados</option>
              <option value="proceso">En Proceso</option>
              <option value="cerrada">Cerrada</option>
              <option value="anulada">Anulada</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Filtrar por</label>
            <select
              value={filters.dateType}
              onChange={(e) => setFilters((prev) => ({ ...prev, dateType: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
            >
              <option value="month">Mes</option>
              <option value="year">Año</option>
              <option value="all">Todos</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() =>
                setFilters({
                  dateType: "month",
                  selectedDate: new Date().toISOString().split("T")[0],
                  estado: "all",
                })
              }
              className="w-full px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Planillas */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        {filteredPlanillas.length === 0 ? (
          <div className="text-center py-12">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No hay planillas</h3>
            <p className="mt-1 text-sm text-gray-500">Comienza creando una nueva planilla.</p>
            <div className="mt-6">
              <button
                onClick={() => setShowModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700"
              >
                <svg
                  className="-ml-1 mr-2 h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                  />
                </svg>
                Nueva Planilla
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Período
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fechas
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Modalidad
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPlanillas.map((planilla) => {
                    const estadoConfig = getEstadoBadge(planilla.estado);
                    const modalidadConfig = getModalidadBadge(planilla.modalidad_planilla);
                    return (
                      <tr key={planilla.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                              <span className="text-white font-bold text-sm">
                                {planilla.nombre.charAt(0)}
                              </span>
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">
                                {planilla.nombre}
                              </div>
                              <div className="text-xs text-gray-500">{planilla.descripcion}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>{formatDateToDDMMYYYY(planilla.fecha_inicio)}</div>
                          <div className="text-xs text-gray-500">
                            {formatDateToDDMMYYYY(planilla.fecha_fin)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}
                          >
                            <span className="mr-1">{estadoConfig.icon}</span>
                            {estadoConfig.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${modalidadConfig.class}`}
                          >
                            {modalidadConfig.text}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleVerDetalle(planilla.id)}
                              className="px-3 py-1 text-xs font-medium rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                              title="Ver Detalle"
                            >
                              Ver Detalle
                            </button>
                            {planilla.estado === "proceso" && (
                              <button
                                onClick={() => handleEliminarPlanilla(planilla.id)}
                                className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                                title="Eliminar Planilla"
                              >
                                Eliminar
                              </button>
                            )}
                            {planilla.estado === "cerrada" && (
                              <button
                                onClick={() => handleAbrirPlanilla(planilla.id)}
                                className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                title="Abrir Planilla"
                              >
                                Abrir
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden divide-y divide-gray-200">
              {filteredPlanillas.map((planilla) => {
                const estadoConfig = getEstadoBadge(planilla.estado);
                const modalidadConfig = getModalidadBadge(planilla.modalidad_planilla);
                return (
                  <div key={planilla.id} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center">
                          <span className="text-white font-bold text-sm">
                            {planilla.nombre.charAt(0)}
                          </span>
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900">{planilla.nombre}</div>
                          <div className="text-xs text-gray-500">{planilla.descripcion}</div>
                        </div>
                      </div>
                      <div className="flex flex-col items-end space-y-1">
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${estadoConfig.class}`}
                        >
                          <span className="mr-1">{estadoConfig.icon}</span>
                          {estadoConfig.text}
                        </span>
                        <span
                          className={`inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full ${modalidadConfig.class}`}
                        >
                          {modalidadConfig.text}
                        </span>
                      </div>
                    </div>

                    <div className="mt-3 text-sm text-gray-600">
                      <div>
                        Período: {formatDateToDDMMYYYY(planilla.fecha_inicio)} -{" "}
                        {formatDateToDDMMYYYY(planilla.fecha_fin)}
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap gap-2">
                      <button
                        onClick={() => handleVerDetalle(planilla.id)}
                        className="px-3 py-1 text-xs font-medium rounded-lg bg-teal-50 text-teal-700 hover:bg-teal-100 transition-colors"
                      >
                        Ver Detalle
                      </button>
                      {planilla.estado === "proceso" && (
                        <button
                          onClick={() => handleEliminarPlanilla(planilla.id)}
                          className="px-3 py-1 text-xs font-medium rounded-lg bg-red-50 text-red-700 hover:bg-red-100 transition-colors"
                        >
                          Eliminar
                        </button>
                      )}
                      {planilla.estado === "cerrada" && (
                        <button
                          onClick={() => handleAbrirPlanilla(planilla.id)}
                          className="px-3 py-1 text-xs font-medium rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                        >
                          Abrir
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Modal para Nueva Planilla */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Nueva Planilla</h3>
              <button onClick={resetModal} className="text-gray-400 hover:text-gray-600">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Nombre *</label>
                <input
                  type="text"
                  name="nombre"
                  value={newPlanilla.nombre}
                  onChange={handleInputChange}
                  placeholder="Nombre de la planilla"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ingresa un nombre descriptivo para la planilla
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción *
                </label>
                <input
                  type="text"
                  name="descripcion"
                  value={newPlanilla.descripcion}
                  onChange={handleInputChange}
                  placeholder="Descripción de la planilla"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Modalidad</label>
                <select
                  name="modalidad_planilla"
                  value={newPlanilla.modalidad_planilla}
                  onChange={handleInputChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                >
                  <option value="planilla_normal">Planilla Normal</option>
                  <option value="planilla_rh">Planilla RH</option>
                  <option value="planilla_mixta">Planilla Mixta</option>
                  <option value="planilla_construccion">Planilla Construcción Civil</option>
                  <option value="planilla_jornales">Planilla de Jornales</option>
                </select>
                {newPlanilla.modalidad_planilla === "planilla_construccion" && (
                  <p className="text-xs text-amber-700 mt-1">
                    Solo personal CONSU. El jornal se calcula por cada día entre las fechas que
                    elijas.
                  </p>
                )}
                {newPlanilla.modalidad_planilla === "planilla_jornales" && (
                  <p className="text-xs text-emerald-700 mt-1">
                    Solo personal con forma de pago jornal (no incluye construcción civil). Elige de
                    qué fecha a qué fecha se paga.
                  </p>
                )}
              </div>

              {esModalidadFechasLibres(newPlanilla.modalidad_planilla) ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Periodo (fecha inicio y fin) *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Inicio</label>
                      <input
                        type="date"
                        name="fecha_inicio"
                        value={newPlanilla.fecha_inicio}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Fin</label>
                      <input
                        type="date"
                        name="fecha_fin"
                        value={newPlanilla.fecha_fin}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Período de la Planilla *
                  </label>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Mes</label>
                      <select
                        name="mes"
                        value={newPlanilla.mes}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      >
                        <option value={1}>Enero</option>
                        <option value={2}>Febrero</option>
                        <option value={3}>Marzo</option>
                        <option value={4}>Abril</option>
                        <option value={5}>Mayo</option>
                        <option value={6}>Junio</option>
                        <option value={7}>Julio</option>
                        <option value={8}>Agosto</option>
                        <option value={9}>Septiembre</option>
                        <option value={10}>Octubre</option>
                        <option value={11}>Noviembre</option>
                        <option value={12}>Diciembre</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Año</label>
                      <select
                        name="año"
                        value={newPlanilla.año}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        required
                      >
                        {/* Generar años desde el año actual hacia atrás y adelante */}
                        {Array.from({ length: 11 }, (_, i) => {
                          const year = new Date().getFullYear() - 5 + i;
                          return (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  {/* Vista previa del período calculado */}
                  <div className="mt-2 p-2 bg-gray-50 rounded-lg border">
                    <p className="text-xs text-gray-600">
                      <span className="font-medium">Período seleccionado:</span>{" "}
                      {getNombreMes(newPlanilla.mes)} {newPlanilla.año}
                    </p>
                    <p className="text-xs text-gray-500">
                      <span className="font-medium">Fechas:</span>{" "}
                      {(() => {
                        const [y1, m1, d1] = periodoPreview.fecha_inicio.split("-").map(Number);
                        const [y2, m2, d2] = periodoPreview.fecha_fin.split("-").map(Number);
                        const fechaInicio = new Date(y1, m1 - 1, d1);
                        const fechaFin = new Date(y2, m2 - 1, d2);
                        return `${formatearFechaDirecta(fechaInicio)} - ${formatearFechaDirecta(fechaFin)}`;
                      })()}
                    </p>
                  </div>
                </div>
              )}

              {newPlanilla.modalidad_planilla === "planilla_rh" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fecha de Cómputo RH (opcional)
                  </label>
                  <select
                    name="computo_rh_id"
                    value={newPlanilla.computo_rh_id}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                  >
                    <option value="">
                      {loadingFechasComputoRh ? "Cargando..." : "Sin cómputo (mes cerrado)"}
                    </option>
                    {fechasComputoRh.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.titulo}
                      </option>
                    ))}
                  </select>
                  {computoRhSeleccionado ? (
                    <p className="text-xs text-gray-500 mt-1">
                      Cómputo del día{" "}
                      {String(computoRhSeleccionado.dia_inicio_computo).padStart(2, "0")} al día
                      anterior al {String(computoRhSeleccionado.dia_fin_computo).padStart(2, "0")}{" "}
                      del mes siguiente (ej.: 15/03–14/04).
                    </p>
                  ) : null}
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={resetModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700"
                >
                  Crear Planilla
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de Confirmación */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        type={confirmModal.type}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onClose={closeConfirmModal}
        showComments={confirmModal.showComments}
        commentsLabel="Comentarios (opcional)"
        commentsPlaceholder="Agregue comentarios sobre el cierre de la planilla..."
      />
    </div>
  );
};

export default Planillas;
